export const MEAL_COUNT_LIMITS = { min: 3, max: 5 };

const GOAL_KEYWORDS = [
  { pattern: /(weight\s*gain|gain\s*weight|bulking|mass)/i, value: 'weight_gain' },
  { pattern: /(muscle\s*(size|gain|growth|building)|hypertrophy|mass)/i, value: 'muscle_size' },
  { pattern: /(weight\s*loss|lose\s*weight|fat\s*loss|fatloss|cutting)/i, value: 'weight_loss' },
  { pattern: /(health|longev|wellness|maintenance)/i, value: 'health' },
];

const BODY_FAT_LEVELS = {
  'men 4-9% / women 10-14%': 'low',
  'men 10-15% / women 15-20%': 'low',
  'men 16-20% / women 21-25%': 'moderate',
  'men 21-30% / women 26-35%': 'high',
  'men 31-40% / women 36-40%': 'high',
  'men 41% / women 41%': 'very-high',
};

const GOAL_DEFAULT_MEAL_COUNTS = {
  weight_loss: { min: 3, max: 5, defaultCount: 3 },
  fat_loss: { min: 3, max: 5, defaultCount: 3 },
  muscle_size: { min: 4, max: 5, defaultCount: 4 },
  weight_gain: { min: 4, max: 5, defaultCount: 5 },
  health: { min: 3, max: 4, defaultCount: 3 },
};

const clampMealCount = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  if (num < MEAL_COUNT_LIMITS.min) return MEAL_COUNT_LIMITS.min;
  if (num > MEAL_COUNT_LIMITS.max) return MEAL_COUNT_LIMITS.max;
  return Math.round(num);
};

const normalizeGoal = (value) => {
  if (!value) return null;
  const text = String(value).trim().toLowerCase();
  if (!text) return null;
  for (const option of GOAL_KEYWORDS) {
    if (option.pattern.test(text)) {
      return option.value;
    }
  }
  return null;
};

const parseBodyFatPercentage = (value = '') => {
  const matches = String(value || '')
    .replace(/[^0-9.\-]/g, ' ')
    .split(/[\s-]+/)
    .map(Number)
    .filter((num) => Number.isFinite(num));
  if (!matches.length) return null;
  const sum = matches.reduce((total, num) => total + num, 0);
  return sum / matches.length;
};

const normalizeBodyFatLevel = (value = '') => {
  if (!value) return null;
  const normalizedKey = value.trim().toLowerCase();
  if (BODY_FAT_LEVELS[normalizedKey]) return BODY_FAT_LEVELS[normalizedKey];
  if (normalizedKey.includes('very-high') || normalizedKey.includes('very high')) return 'very-high';
  if (normalizedKey.includes('low')) return 'low';
  if (normalizedKey.includes('high')) return 'high';
  if (normalizedKey.includes('moderate')) return 'moderate';
  const numeric = parseBodyFatPercentage(value);
  if (!Number.isFinite(numeric)) return null;
  if (numeric <= 15) return 'low';
  if (numeric <= 20) return 'moderate';
  if (numeric <= 40) return 'high';
  return 'very-high';
};

export function deriveMealCountGuidance({
  primaryGoal,
  bodyFatEstimate,
} = {}) {
  const normalizedGoals = [
    normalizeGoal(primaryGoal),
  ].filter(Boolean);
  const goalSet = new Set(normalizedGoals);
  const hasGoal = (goal) => goalSet.has(goal);
  const hasWeightLoss = hasGoal('weight_loss');
  const hasMuscleSize = hasGoal('muscle_size');
  const hasHealth = hasGoal('health');
  const hasWeightGain = hasGoal('weight_gain');
  const bodyFatLevel = normalizeBodyFatLevel(bodyFatEstimate);
  const reasons = [];
  let recommended = null;
  let rangeMin = MEAL_COUNT_LIMITS.min;
  let rangeMax = MEAL_COUNT_LIMITS.max;

  const assignDecision = (count, minRange, maxRange, note) => {
    const safeMin = Math.max(MEAL_COUNT_LIMITS.min, Math.min(MEAL_COUNT_LIMITS.max, minRange));
    const safeMax = Math.max(safeMin, Math.min(MEAL_COUNT_LIMITS.max, maxRange));
    rangeMin = safeMin;
    rangeMax = safeMax;
    recommended = count;
    if (note) reasons.push(note);
    reasons.push(`Goal window: ${safeMin}-${safeMax} meals.`);
  };

  if (hasWeightGain) {
    assignDecision(
      MEAL_COUNT_LIMITS.max,
      MEAL_COUNT_LIMITS.max,
      MEAL_COUNT_LIMITS.max,
      'Weight Gain goal caps meal frequency at five meals.'
    );
  } else if (bodyFatLevel === 'very-high') {
    assignDecision(
      MEAL_COUNT_LIMITS.min,
      MEAL_COUNT_LIMITS.min,
      MEAL_COUNT_LIMITS.min,
      'Very high body-fat → keep the day to three anchored meals.'
    );
  } else if (bodyFatLevel === 'high') {
    assignDecision(
      MEAL_COUNT_LIMITS.min,
      MEAL_COUNT_LIMITS.min,
      MEAL_COUNT_LIMITS.min,
      'High body-fat → keep the day to three anchored meals.'
    );
  } else if (hasWeightLoss && hasMuscleSize) {
    if (bodyFatLevel === 'low') {
      assignDecision(
        MEAL_COUNT_LIMITS.max,
        4,
        MEAL_COUNT_LIMITS.max,
        'Weight Loss/Fatloss + Muscle Size at low body-fat → emphasize the upper range.'
      );
    } else {
      assignDecision(
        4,
        MEAL_COUNT_LIMITS.min,
        4,
        'Weight Loss/Fatloss + Muscle Size defaults to four structured meals.'
      );
    }
  } else if (hasMuscleSize) {
    if (bodyFatLevel === 'low') {
      assignDecision(
        MEAL_COUNT_LIMITS.max,
        4,
        MEAL_COUNT_LIMITS.max,
        'Low body-fat muscle focus → prefer the upper end of the 4-5 meal window.'
      );
    } else {
      assignDecision(
        GOAL_DEFAULT_MEAL_COUNTS.muscle_size.defaultCount,
        4,
        MEAL_COUNT_LIMITS.max,
        'Muscle Size goal defaults to four substantial meals (4-5 range).'
      );
    }
  } else if (hasWeightLoss && hasHealth) {
    if (bodyFatLevel === 'low') {
      assignDecision(
        4,
        MEAL_COUNT_LIMITS.min,
        4,
        'Weight Loss/Fatloss + Health with low body-fat → use four balanced meals.'
      );
    } else {
      assignDecision(
        MEAL_COUNT_LIMITS.min,
        MEAL_COUNT_LIMITS.min,
        4,
        'Weight Loss/Fatloss + Health defaults to the lower end (3-4 meals).'
      );
    }
  } else if (hasWeightLoss) {
    if (bodyFatLevel === 'low') {
      assignDecision(
        4,
        MEAL_COUNT_LIMITS.min,
        4,
        'Low body-fat weight loss → favor the upper end (four meals).'
      );
    } else {
      assignDecision(
        GOAL_DEFAULT_MEAL_COUNTS.weight_loss.defaultCount,
        MEAL_COUNT_LIMITS.min,
        4,
        'Weight Loss/Fatloss defaults to three meals (range 3-4).'
      );
    }
  } else if (hasHealth) {
    if (bodyFatLevel === 'low') {
      assignDecision(
        4,
        MEAL_COUNT_LIMITS.min,
        4,
        'Health/Longevity with low body-fat → lean on four evenly spaced meals.'
      );
    } else {
      assignDecision(
        GOAL_DEFAULT_MEAL_COUNTS.health.defaultCount,
        MEAL_COUNT_LIMITS.min,
        4,
        'Health/Longevity defaults to three meals (3-4 range).'
      );
    }
  }

  if (recommended == null) {
    const fallbackGoal = normalizedGoals[0] || normalizedGoals[1] || null;
    if (fallbackGoal && GOAL_DEFAULT_MEAL_COUNTS[fallbackGoal]) {
      const defaults = GOAL_DEFAULT_MEAL_COUNTS[fallbackGoal];
      assignDecision(
        defaults.defaultCount,
        defaults.min,
        defaults.max,
        'No combo rules matched → applying primary-goal defaults.'
      );
    } else {
      assignDecision(4, MEAL_COUNT_LIMITS.min, 4, 'Fallback to general default (four meals within a 3-4 range).');
    }
  }

  return {
    suggestedCount: clampMealCount(recommended) ?? rangeMax ?? MEAL_COUNT_LIMITS.max,
    rangeMin,
    rangeMax,
    reason: reasons.join(' '),
  };
}
